import { WS_URL } from "../constants/constant";
import authService from "./auth.service";

/**
 * ChatWebSocketService
 *
 * Manages a single WebSocket connection to the AWS API Gateway WebSocket API.
 * Provides:
 *  - connect() / disconnect()
 *  - sendMessage(toUserId, text)
 *  - onMessage(callback)   — register a handler for incoming messages
 *  - Automatic reconnect with exponential backoff
 */
class ChatWebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.statusListeners = new Set();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
    this.shouldReconnect = false;
    this.status = "disconnected"; // disconnected | connecting | connected
    this.pendingQueue = []; // messages queued while connecting
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /** Connect to the WebSocket API using the current Cognito ID token */
  async connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;
    if (this.status === "connecting") return; // already in progress

    // Mark as connecting immediately (before the async refresh)
    // so that any sendMessage() called concurrently will queue correctly.
    this.shouldReconnect = true;
    this._setStatus("connecting");

    // Refresh token so we never connect with an expired one.
    try {
      await authService.refreshTokens();
    } catch (err) {
      console.warn('[ChatWS] Token refresh failed:', err.message);
    }

    const token = authService.getIdToken();
    if (!token) {
      console.warn("[ChatWS] No auth token — cannot connect");
      this._setStatus("disconnected");
      return;
    }

    const url = `${WS_URL}?token=${encodeURIComponent(token)}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log("[ChatWS] Connected");
      this.reconnectAttempts = 0;
      this._setStatus("connected");
      // Flush any messages that were queued while connecting
      if (this.pendingQueue.length > 0) {
        console.log(`[ChatWS] Flushing ${this.pendingQueue.length} queued message(s)`);
        const queue = [...this.pendingQueue];
        this.pendingQueue = [];
        queue.forEach(({ toUserId, text }) => {
          this.ws.send(JSON.stringify({ action: "sendMessage", toUserId, text }));
        });
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this._dispatch(data);
      } catch (err) {
        console.warn("[ChatWS] Could not parse message", err);
      }
    };

    this.ws.onerror = (err) => {
      console.error("[ChatWS] Error:", err);
    };

    this.ws.onclose = (event) => {
      console.log(`[ChatWS] Closed code=${event.code}`);
      this._setStatus("disconnected");
      if (this.shouldReconnect) {
        this._scheduleReconnect();
      }
    };
  }

  /** Cleanly disconnect — disables auto-reconnect */
  disconnect() {
    this.shouldReconnect = false;
    clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.close(1000, "Client disconnected");
      this.ws = null;
    }
    this._setStatus("disconnected");
  }

  /**
   * Send a chat message
   * @param {string} toUserId - recipient's Cognito sub (userId)
   * @param {string} text
   */
  sendMessage(toUserId, text) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: "sendMessage", toUserId, text }));
      return true;
    }
    // In any non-OPEN state, queue the message and ensure we're connecting
    console.log(`[ChatWS] Queuing message (readyState=${this.ws?.readyState ?? 'null'}, status=${this.status})`);
    this.pendingQueue.push({ toUserId, text });
    // Trigger a connect if we're not already in progress
    if (this.ws?.readyState !== WebSocket.CONNECTING) {
      this.connect().catch(console.error);
    }
    return true;
  }

  /**
   * Register a handler for incoming messages
   * Callback receives: { type: "message"|"ack", message: {...} }
   * @param {Function} callback
   * @returns {Function} unsubscribe function
   */
  onMessage(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Register a handler for connection status changes
   * Callback receives: "disconnected" | "connecting" | "connected"
   * @param {Function} callback
   * @returns {Function} unsubscribe function
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);
    callback(this.status); // immediately emit current status
    return () => this.statusListeners.delete(callback);
  }

  get isConnected() {
    return this.status === "connected";
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  _dispatch(data) {
    this.listeners.forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error("[ChatWS] Listener error:", err);
      }
    });
  }

  _setStatus(status) {
    this.status = status;
    this.statusListeners.forEach((cb) => cb(status));
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn("[ChatWS] Max reconnect attempts reached");
      return;
    }
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 16000);
    this.reconnectAttempts += 1;
    console.log(`[ChatWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect().catch(console.error), delay);
  }
}

// Export a singleton — shared across all components
const chatWebSocket = new ChatWebSocketService();
export default chatWebSocket;
